import { Injectable } from '@angular/core';
import { Constants } from '../shared/constants/create-survey.constants';
import { QUALIFICATION_CODES } from '../shared/constants/qualification-code';
import { QUALIFICATIONS_CATEGORIES } from '../shared/constants/qualification-categories';
import { CURRENCY_CODE } from '../shared/constants/currency-units';
import { CategoriesInterface } from '../shared/interfaces/create-surveys.interface';
import {
  Qualification as SurveyQualification,
  Quotas,
  Quotas as SurveyQuota,
} from '../shared/interfaces/survey.interface';
import {
  AdvanceTargetingQualification,
  Qualification,
} from '../shared/interfaces/qualification.interface';
import {
  EditQuota,
  QuotaPayload,
  SelectedQuota,
} from '../shared/interfaces/quota.interface';

@Injectable({
  providedIn: 'root',
})
export class EditSurveyService {
  readonly QUALIFICATIONS_RENDER_TYPE = Constants.QUALIFICATIONS_RENDER_TYPE;
  readonly ADVANCE_TARGETING_QUAL_ID_START_INDEX =
    Constants.ADVANCE_TARGETING_QUAL_ID_START_INDEX;
  constructor() {}

  populateCategory(
    categories: CategoriesInterface[],
    survey_category_code: number
  ) {
    let matchedCatIndex = categories.findIndex(
      (cat) => cat.id === survey_category_code
    );
    if (matchedCatIndex < 0) {
      // if not found in the categories then return first category
      matchedCatIndex = 0;
    }
    const category = categories[matchedCatIndex];
    category.selected = true;
    return category;
  }

  populateCoreQualifications(
    qualifications: Qualification[] = [],
    surveyQualifications: SurveyQualification[] = [],
    advanceQualifications: AdvanceTargetingQualification[] = [],
    surveyQuotas: SurveyQuota[] = []
  ) {
    return (
      qualifications
        //.filter((qualification) => !qualification.is_location)
        .map((qualification) => {
          const surveyQualification = (surveyQualifications ?? []).find(
            (_surveyQualification) => {
              return (
                _surveyQualification.qualification_code ===
                qualification.qualification_code
              );
            }
          );
          if (qualification.is_range && surveyQualification) {
            // populate qualification range
            const range = surveyQualification?.range_sets.reduce(
              (acc: any, current: any) => {
                if (current.from < acc.mn || !acc.hasOwnProperty('mn')) {
                  acc.mn = current.from;
                }
                if (current.to > acc.mx || !acc.hasOwnProperty('mx')) {
                  acc.mx = current.to;
                }
                return acc;
              },
              {}
            );
            if (!surveyQualification.isDefault) {
              qualification.data[0].minValue = range.mn;
              qualification.data[0].maxValue = range.mx;
            }
          } else if (surveyQualification) {
            // populate selected qualifications options
            qualification.data = this._mapOptionSelected(
              qualification,
              surveyQualification
            );
          }
          // populate "layered" qualification quotas
          const isChild =
            qualification.qualification_code === QUALIFICATION_CODES.CHILDREN;
          qualification.quotas = this._getAppliedQuotas(
            surveyQuotas,
            qualification.qualification_code,
            qualification.is_range,
            isChild
          );
          qualification.flexible = this._getQuotasFlexibility(
            qualification.quotas
          );

          qualification.isAdvanceTargetingParent =
            this._checkIfAdvanceTargetingParent(
              qualification,
              advanceQualifications
            );
          return qualification;
        })
    );
  }

  private _checkIfAdvanceTargetingParent(
    qualification: Qualification,
    advanceQualifications: AdvanceTargetingQualification[]
  ) {
    return advanceQualifications.some(
      (advanceQualification) =>
        advanceQualification &&
        advanceQualification.hasDependency &&
        advanceQualification.dependency &&
        advanceQualification.dependency.parentQualId &&
        advanceQualification.dependency.parentQualId ===
          qualification.db_qualification_code
    );
  }

  populateLocationQualification(
    qualifications: Qualification[] = [],
    surveyQualifications: SurveyQualification[] = [],
    surveyQuotas: SurveyQuota[] = []
  ) {
    const locationQualification = qualifications
      .filter((qualification) => qualification.is_location)
      .find((qualification) => {
        return surveyQualifications.find((surveyQualification) => {
          return (
            surveyQualification.qualification_code ===
            qualification.qualification_code
          );
        });
      });

    if (locationQualification) {
      const surveyQualification = surveyQualifications.find(
        (_surveyQualification) => {
          return (
            _surveyQualification.qualification_code ===
            locationQualification.qualification_code
          );
        }
      );
      if (
        locationQualification.render_type ===
        this.QUALIFICATIONS_RENDER_TYPE.MULTIPUNCH_LIST
      ) {
        locationQualification.data = this._mapOptionSelected(
          locationQualification,
          surveyQualification
        );
      } else if (
        (locationQualification.render_type ===
          this.QUALIFICATIONS_RENDER_TYPE.SEARCH ||
          locationQualification.render_type ===
            this.QUALIFICATIONS_RENDER_TYPE.FILE_UPLOAD) &&
        surveyQualification?.data?.length
      ) {
        locationQualification.data = surveyQualification?.data.map((item) => {
          item.selected = true;
          return item;
        });
      }
      // populate "layered" qualification quotas
      this.populateLocationQuotas(surveyQuotas, locationQualification);
      return locationQualification;
    }
    return;
  }

  populateLocationQuotas(
    quotas: Quotas[],
    locationQualification: Qualification
  ) {
    const isChild =
      locationQualification.qualification_code === QUALIFICATION_CODES.CHILDREN;
    locationQualification.quotas = this._getAppliedQuotas(
      quotas,
      locationQualification.qualification_code,
      locationQualification.is_range,
      isChild
    );
    locationQualification.flexible = this._getQuotasFlexibility(
      locationQualification.quotas
    );
    return locationQualification;
  }

  _getGroupedQuotas(quotas: SurveyQuota[], qualificationCode: number) {
    return quotas
      .filter((quota) => {
        return (
          quota.quota_category === QUALIFICATIONS_CATEGORIES.GROUPED &&
          quota.criteria[0].qualification_code === qualificationCode
        );
      })
      .map((quota) => {
        return {
          options: quota.criteria[0].condition_codes,
          text: quota.criteria[0].group_name,
          selected: true,
          number: quota.required_count,
          percentage: quota.percentage,
          minimum: quota.minimum,
          maximum: quota.maximum,
          flexible: quota.flexible,
          buyer_quota_id: quota.buyer_quota_id,
          suppliers: quota.suppliers,
        };
      });
  }

  populateAdvanceQualifications(
    surveyQualifications: SurveyQualification[] = [],
    surveyQuotas: SurveyQuota[] = [],
    qualifications: Qualification[] = []
  ) {
    const advanceQualifications: AdvanceTargetingQualification[] = [];
    surveyQualifications.forEach((qual) => {
      if (
        qual.qualification_code >= this.ADVANCE_TARGETING_QUAL_ID_START_INDEX
      ) {
        // if AT question is selected as position front view or location then do not show it in adv targetting.
        const excludedFromAdvanceQualification = qualifications.find(
          (qualification) =>
            qualification.qualification_code === qual.qualification_code
        );
        if (excludedFromAdvanceQualification) {
          return;
        }
        const selectedAnswers = qual.data.map((answer) => {
          return {
            id: Number(answer.id),
            text: answer.name as string,
          };
        });
        const selectedQuotas: EditQuota[] = this._getAppliedQuotas(
          surveyQuotas,
          qual.qualification_code,
          false,
          false
        );
        const flexible = this._getQuotasFlexibility(selectedQuotas);

        advanceQualifications.push({
          flexible,
          qualification_name: qual.qualification_name,
          qualification_code: qual.qualification_code,
          isRangeType: qual.isRangeType,
          rangeSet: qual.range_sets?.length ? qual.range_sets[0] : undefined,
          selectedAnswers,
          hasDependency: qual.hasDependency,
          ...(qual.dependency && { dependency: qual.dependency }),
          childs: this._generateChildArrayForAdvanceParent(
            surveyQualifications,
            qual
          ),
          quotas: selectedQuotas,
        });
      }
    });
    return advanceQualifications;
  }

  private _generateChildArrayForAdvanceParent(
    surveyQualifications: SurveyQualification[],
    advanceQualification: SurveyQualification
  ) {
    const advanceParentWithChild = surveyQualifications.find(
      (qual) =>
        qual &&
        qual.qualification_code &&
        qual.qualification_code >= this.ADVANCE_TARGETING_QUAL_ID_START_INDEX &&
        qual.hasDependency &&
        qual.dependency &&
        qual.dependency.parentQualId === advanceQualification.qualification_code
    );
    if (!advanceParentWithChild) {
      return [];
    }
    return [advanceParentWithChild.qualification_code];
  }

  populateQuotas(surveyQuotas: SurveyQuota[] = []) {
    return surveyQuotas.map((quota) => {
      return {
        buyer_quota_id: quota.buyer_quota_id,
        required_count: quota.required_count,
        flexible: quota.flexible,
        criteria: quota.criteria,
        percentage: quota.percentage,
        maximum: quota.maximum,
        minimum: quota.minimum,
        suppliers: quota.suppliers,
        quota_category: quota.quota_category,
        current_target: quota.current_target,
        individual_quotas: quota.individual_quotas,
        quota_title: quota.quota_title,
      };
    });
  }

  private _getQuotasFlexibility(quotas: SelectedQuota[]) {
    if (quotas.length) {
      return quotas[0].flexible;
    } else {
      return {
        enable: true,
        value: 0,
      };
    }
  }

  private _getAppliedQuotas(
    quotas: SurveyQuota[],
    qualificationCode: number,
    isRange: boolean,
    isChildQualification: boolean
  ) {
    return quotas
      .filter((quota) => {
        return (
          [
            QUALIFICATIONS_CATEGORIES.LAYERED,
            QUALIFICATIONS_CATEGORIES.ADVANCE,
            QUALIFICATIONS_CATEGORIES.GROUPED,
          ].includes(quota.quota_category) &&
          quota.criteria.find(
            (item) => item.qualification_code === qualificationCode
          )
        );
      })
      .map((quota) => {
        const outQuota: EditQuota = {
          options: quota.criteria[0].condition_codes,
          selected: true,
          number: quota.required_count,
          buyer_quota_id: quota.buyer_quota_id,
          percentage: quota.percentage,
          minimum: quota.minimum,
          maximum: quota.maximum,
          flexible: quota.flexible,
          suppliers: quota.suppliers,
        };
        if (isRange) {
          outQuota.min = quota.criteria[0].range_sets?.length
            ? quota.criteria[0].range_sets[0].from
            : 0;
          outQuota.max = quota.criteria[0].range_sets?.length
            ? quota.criteria[0].range_sets[0].to
            : 0;
        } else {
          outQuota.text = quota.criteria[0].group_name
            ? quota.criteria[0].group_name
            : quota.criteria[0].condition_names?.length
            ? quota.criteria[0].condition_names.join(
                Constants.GROUP_QUOTA_SEPARATOR
              )
            : '';
          outQuota.oldQuotasText = quota.criteria[0].condition_names?.length
            ? quota.criteria[0].condition_names
            : [];
        }

        if (isChildQualification) {
          this._mapAppliedQuotaForChildQualification(quota, outQuota);
        }
        if (quota.quota_category === QUALIFICATIONS_CATEGORIES.GROUPED) {
          outQuota.selectedForGroup = true;
        }
        return outQuota;
      });
  }

  private _mapAppliedQuotaForChildQualification(
    quota: Quotas,
    outQuota: EditQuota
  ) {
    const childGender = quota.criteria.find((singleCriteria) => {
      return (
        singleCriteria.qualification_code == QUALIFICATION_CODES.CHILD_GENDER
      );
    });
    if (childGender && childGender.condition_codes.length) {
      // have children gender exist in quota
      outQuota.gender =
        childGender.condition_codes.length > 1
          ? Constants.CHILD_GENDER.BOTH
          : childGender.condition_codes[0];
    }
    const childAge = quota.criteria.find((singleCriteria) => {
      return singleCriteria.qualification_code == QUALIFICATION_CODES.CHILD_AGE;
    });
    if (childAge && childAge.range_sets.length) {
      // have children age exist in quota
      outQuota.min = childAge.range_sets[0].from;
      outQuota.max = childAge.range_sets[0].to;
      outQuota.ageunit = childAge.range_sets[0].units;
    }
  }

  private _mapOptionSelected(
    qualification: Qualification,
    surveyQualification: SurveyQualification | undefined
  ) {
    return qualification.data.map((option) => {
      if (
        surveyQualification?.condition_codes?.includes(option.id.toString())
      ) {
        option.selected = true;
      } else {
        option.selected = false;
      }
      return option;
    });
  }

  convertQualificationsIntoSurveyQualifications(
    newQualifications: Qualification[],
    qualifications: Qualification[],
    quotas: QuotaPayload[]
  ): SurveyQualification[] {
    return qualifications
      .filter((qualification) => !qualification.is_location)
      .map((qualification) => {
        return {
          qualification_code: qualification.qualification_code,
          qualification_name: qualification.qualification_name,
          condition_codes: qualification.data
            .filter((item) => item.selected)
            .map((item) => `${item.id}`),
          range_sets: qualification.data
            .filter(
              (item) =>
                (item.minValue || item.minValue === 0) && // item.minValue===0 check for income
                item.maxValue
            )
            .map((item) => {
              return {
                from: +item.minValue,
                to: +item.maxValue,
                units: CURRENCY_CODE.US, // hardcodes since these are not used in populateCoreQualifications function but required for interface
              };
            }),
          isDefault: this._getIsDefault(
            newQualifications,
            qualification,
            quotas
          ),
          key: qualification.qualification_name,
          isAllSet: 1, // hardcodes since these are not used in populateCoreQualifications function  but required for interface
          isQuotaSet: 1, // hardcodes since these are not used in populateCoreQualifications function  but required for interface
          isRangeType: +qualification.is_range,
          data: [],
        };
      });
  }

  identifyLostQualifications(
    newQualifications: Qualification[],
    preQualifications: Qualification[],
    preQuotas: QuotaPayload[]
  ): Array<string> {
    const lostQualifications: string[] = [];
    preQualifications
      .filter((preQualification) => !preQualification.is_location)
      .forEach((preQualification) => {
        const isMatchedQualification = newQualifications.find(
          (newQualification) =>
            newQualification.qualification_code ===
            preQualification.qualification_code
        );
        if (
          !isMatchedQualification &&
          this.qualificationHasQuotas(preQualification, preQuotas)
        ) {
          lostQualifications.push(
            preQualification.qualification_name.toUpperCase()
          );
        }
      });
    return lostQualifications;
  }

  qualificationHasQuotas(
    qualification: Qualification,
    quotas: QuotaPayload[] = []
  ) {
    let hasQuota = false;
    quotas.forEach((quota) => {
      if (
        quota.criteria.filter(
          (crit) => crit.qualification_code === qualification.qualification_code
        ).length
      ) {
        hasQuota = true;
      }
    });
    return hasQuota;
  }

  private _getIsDefault(
    newqualifications: Qualification[],
    qualification: Qualification,
    quotas: QuotaPayload[]
  ) {
    if (
      this._checkDefaultQualification(newqualifications, qualification) &&
      !this._checkSelectedQuota(quotas, qualification)
    ) {
      return 1;
    }
    return 0;
  }

  private _checkDefaultQualification(
    newqualifications: Qualification[],
    qualification: Qualification
  ) {
    if (qualification.is_range) {
      return newqualifications.some((newqualification) => {
        return this._isDefaultRangeTypeQualification(
          newqualification,
          qualification
        );
      });
    }
    return this._chekQualificationDataLength(newqualifications, qualification);
  }

  private _chekQualificationDataLength(
    newqualifications: Qualification[],
    qualification: Qualification
  ) {
    const qual = newqualifications.find(
      (newqualification) =>
        newqualification.qualification_code === qualification.qualification_code
    );
    if (qual?.data.length !== qualification.data.length) {
      return false;
    }
    return true;
  }

  private _checkSelectedQuota(
    quotas: QuotaPayload[],
    qualification: Qualification
  ) {
    return quotas.some((quota) => {
      return quota.criteria.find(
        (item) => item.qualification_code === qualification.qualification_code
      );
    });
  }

  private _isDefaultRangeTypeQualification(
    newQualificationData: Qualification,
    qualificationData: Qualification
  ) {
    return [
      () => qualificationData,
      () => newQualificationData,
      () => qualificationData.data,
      () => newQualificationData.data,
      () => qualificationData.data.length,
      () => newQualificationData.data.length,
      () => qualificationData.data[0].hasOwnProperty('minValue'),
      () => newQualificationData.data[0].hasOwnProperty('minValue'),
      () =>
        qualificationData.data[0].minValue ===
        newQualificationData.data[0].minValue,
      () => qualificationData.data[0].hasOwnProperty('maxValue'),
      () => newQualificationData.data[0].hasOwnProperty('maxValue'),
      () =>
        qualificationData.data[0].maxValue ===
        newQualificationData.data[0].maxValue,
    ].every((item) => !!item());
  }
}
